import { useCallback, useEffect, useMemo, useState } from 'react'
import { supabase } from '../supabaseClient'
import EmptyState from '../ui/EmptyState'
import ErrorState from '../ui/ErrorState'
import LoadingState from '../ui/LoadingState'
import SectionHeader from '../ui/SectionHeader'
import StatusBadge from '../ui/StatusBadge'
import { formatPlainText, formatTitleText } from '../utils/displayText'
import { buildSchemaUnavailableMessage, isSchemaMismatchError } from './schemaGuard'

const CONTACT_TYPE_OPTIONS = [
  { value: 'site', label: 'Site Contact' },
  { value: 'job', label: 'Job Contact' },
  { value: 'client', label: 'Client' },
  { value: 'vendor', label: 'Vendor' },
  { value: 'internal', label: 'Internal' }
]

const METHOD_OPTIONS = [
  { value: 'phone', label: 'Phone' },
  { value: 'email', label: 'Email' },
  { value: 'text', label: 'Text' }
]

const createBlankForm = () => ({
  full_name: '',
  company_name: '',
  role_title: '',
  phone: '',
  email: '',
  contact_type: 'site',
  preferred_contact_method: 'phone',
  linked_site_id: '',
  linked_job_id: '',
  notes: '',
  active_status: true
})

const formatDateTime = (value) => {
  if (!value) return 'Unknown'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return 'Unknown'
  return date.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

const mapContactRow = (row) => ({
  ...row,
  full_name: formatTitleText(row.full_name, 'Unnamed Contact'),
  company_name: formatTitleText(row.company_name, ''),
  role_title: formatTitleText(row.role_title, ''),
  phone: formatPlainText(row.phone, ''),
  email: formatPlainText(row.email, ''),
  contact_type: String(row.contact_type || 'site').toLowerCase(),
  preferred_contact_method: String(row.preferred_contact_method || 'phone').toLowerCase(),
  active_status: row.active_status !== false
})

const toFormFromContact = (contact) => ({
  full_name: contact.full_name || '',
  company_name: contact.company_name || '',
  role_title: contact.role_title || '',
  phone: contact.phone || '',
  email: contact.email || '',
  contact_type: contact.contact_type || 'site',
  preferred_contact_method: contact.preferred_contact_method || 'phone',
  linked_site_id: contact.linked_site_id ? String(contact.linked_site_id) : '',
  linked_job_id: contact.linked_job_id ? String(contact.linked_job_id) : '',
  notes: contact.notes || '',
  active_status: contact.active_status !== false
})

export default function ContactsAdminPanel({ onDataChanged }) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')
  const [contactsUnavailableMessage, setContactsUnavailableMessage] = useState('')

  const [sites, setSites] = useState([])
  const [jobs, setJobs] = useState([])
  const [contacts, setContacts] = useState([])

  const [editingContactId, setEditingContactId] = useState('')
  const [form, setForm] = useState(createBlankForm)

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    setContactsUnavailableMessage('')

    const [contactsResponse, sitesResponse, jobsResponse] = await Promise.all([
      supabase
        .from('contacts')
        .select('*')
        .order('active_status', { ascending: false })
        .order('created_at', { ascending: false }),
      supabase.from('sites').select('id, name').order('name'),
      supabase.from('jobs').select('id, job_type, job_date, site_id').order('job_date', { ascending: false }).limit(150)
    ])

    const contactsSchemaMismatch = isSchemaMismatchError(contactsResponse.error)

    if ((contactsResponse.error && !contactsSchemaMismatch) || sitesResponse.error || jobsResponse.error) {
      setContacts([])
      setSites([])
      setJobs([])
      setError(
        contactsResponse.error?.message ||
          sitesResponse.error?.message ||
          jobsResponse.error?.message ||
          'Unable to load contacts panel data.'
      )
      setLoading(false)
      return
    }

    if (contactsSchemaMismatch) {
      setContactsUnavailableMessage(buildSchemaUnavailableMessage('Contacts', 'Workers/Jobs'))
      setContacts([])
    } else {
      setContacts((contactsResponse.data || []).map(mapContactRow))
    }
    setSites(sitesResponse.data || [])
    setJobs(jobsResponse.data || [])
    setLoading(false)
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const siteById = useMemo(() => new Map(sites.map((site) => [String(site.id), site])), [sites])
  const jobById = useMemo(() => new Map(jobs.map((job) => [String(job.id), job])), [jobs])

  const activeContacts = useMemo(() => contacts.filter((contact) => contact.active_status !== false), [contacts])

  const handleChange = (field, value) => {
    setForm((previous) => ({
      ...previous,
      [field]: value
    }))
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    if (contactsUnavailableMessage) {
      setMessage(contactsUnavailableMessage)
      return
    }

    const fullName = formatTitleText(form.full_name)
    if (!fullName) {
      setMessage('Full name is required.')
      return
    }

    setSaving(true)
    setMessage('')

    const payload = {
      full_name: fullName,
      company_name: formatTitleText(form.company_name),
      role_title: formatTitleText(form.role_title),
      phone: formatPlainText(form.phone),
      email: formatPlainText(form.email).toLowerCase(),
      contact_type: String(form.contact_type || 'site').toLowerCase(),
      preferred_contact_method: String(form.preferred_contact_method || 'phone').toLowerCase(),
      linked_site_id: form.linked_site_id || null,
      linked_job_id: form.linked_job_id || null,
      notes: formatPlainText(form.notes),
      active_status: form.active_status !== false
    }

    let response
    if (editingContactId) {
      response = await supabase.from('contacts').update(payload).eq('id', editingContactId)
    } else {
      response = await supabase.from('contacts').insert([payload])
    }

    if (response.error) {
      setSaving(false)
      setMessage('Unable to save contact: ' + response.error.message)
      return
    }

    setForm(createBlankForm())
    setEditingContactId('')
    setSaving(false)
    setMessage(editingContactId ? 'Contact updated.' : 'Contact created.')
    await load()
    onDataChanged?.()
  }

  const handleEdit = (contact) => {
    setEditingContactId(String(contact.id))
    setForm(toFormFromContact(contact))
    setMessage('')
  }

  const handleCancelEdit = () => {
    setEditingContactId('')
    setForm(createBlankForm())
  }

  const handleToggleActive = async (contact) => {
    if (!contact?.id) return
    if (contactsUnavailableMessage) {
      setMessage(contactsUnavailableMessage)
      return
    }

    setSaving(true)
    setMessage('')

    const response = await supabase
      .from('contacts')
      .update({ active_status: !(contact.active_status !== false) })
      .eq('id', contact.id)

    if (response.error) {
      setSaving(false)
      setMessage('Unable to update contact state: ' + response.error.message)
      return
    }

    setSaving(false)
    setMessage('Contact state updated.')
    await load()
    onDataChanged?.()
  }

  if (loading) {
    return <LoadingState title="Loading contacts" message="Preparing contact records and links." />
  }

  if (error) {
    return <ErrorState title="Contacts unavailable" message={error} />
  }

  return (
    <section className="gg-module-stack">
      <section className="gg-module-panel">
        <SectionHeader
          eyebrow="Admin"
          title="Contact Management"
          description="Create, edit, link, and activate/deactivate contacts for site and job clarity."
        />
        <div className="gg-tool-pills">
          <span className="gg-inline-chip">Total {contacts.length}</span>
          <span className="gg-inline-chip">Active {activeContacts.length}</span>
        </div>
        {contactsUnavailableMessage ? <p className="gg-inline-alert gg-inline-alert--warning">{contactsUnavailableMessage}</p> : null}
      </section>

      <section className="gg-module-panel">
        <p className="gg-module-panel__label">{editingContactId ? 'Edit Contact' : 'Create Contact'}</p>
        {contactsUnavailableMessage ? (
          <EmptyState
            title="Contacts data source unavailable"
            message="The contacts table is not available in this environment yet. Continue testing with workers, jobs, and time workflows."
          />
        ) : (
          <form className="gg-form-grid" onSubmit={handleSubmit}>
            <label className="gg-form-field">
              <span>Full Name *</span>
              <input className="gg-input" value={form.full_name} onChange={(event) => handleChange('full_name', event.target.value)} />
            </label>
            <label className="gg-form-field">
              <span>Company Name</span>
              <input className="gg-input" value={form.company_name} onChange={(event) => handleChange('company_name', event.target.value)} />
            </label>
            <label className="gg-form-field">
              <span>Role Title</span>
              <input className="gg-input" value={form.role_title} onChange={(event) => handleChange('role_title', event.target.value)} />
            </label>
            <label className="gg-form-field">
              <span>Phone</span>
              <input className="gg-input" value={form.phone} onChange={(event) => handleChange('phone', event.target.value)} />
            </label>
            <label className="gg-form-field">
              <span>Email</span>
              <input className="gg-input" type="email" value={form.email} onChange={(event) => handleChange('email', event.target.value)} />
            </label>
            <label className="gg-form-field">
              <span>Contact Type</span>
              <select className="gg-select" value={form.contact_type} onChange={(event) => handleChange('contact_type', event.target.value)}>
                {CONTACT_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="gg-form-field">
              <span>Preferred Contact Method</span>
              <select
                className="gg-select"
                value={form.preferred_contact_method}
                onChange={(event) => handleChange('preferred_contact_method', event.target.value)}
              >
                {METHOD_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="gg-form-field">
              <span>Linked Site</span>
              <select className="gg-select" value={form.linked_site_id} onChange={(event) => handleChange('linked_site_id', event.target.value)}>
                <option value="">Not linked</option>
                {sites.map((site) => (
                  <option key={site.id} value={site.id}>
                    {formatTitleText(site.name, 'Unnamed Site')}
                  </option>
                ))}
              </select>
            </label>
            <label className="gg-form-field">
              <span>Linked Job</span>
              <select className="gg-select" value={form.linked_job_id} onChange={(event) => handleChange('linked_job_id', event.target.value)}>
                <option value="">Not linked</option>
                {jobs.map((job) => (
                  <option key={job.id} value={job.id}>
                    {formatTitleText(job.job_type, 'Job')} • {job.job_date || 'No date'}
                  </option>
                ))}
              </select>
            </label>
            <label className="gg-form-field">
              <span>Notes</span>
              <textarea className="gg-textarea" value={form.notes} onChange={(event) => handleChange('notes', event.target.value)} />
            </label>
            <label className="gg-form-field">
              <span>Active</span>
              <input
                type="checkbox"
                checked={form.active_status !== false}
                onChange={(event) => handleChange('active_status', event.target.checked)}
              />
            </label>
            <div className="gg-form-actions">
              <button type="submit" className="gg-button gg-button--primary" disabled={saving}>
                {saving ? 'Saving...' : editingContactId ? 'Save Contact' : 'Create Contact'}
              </button>
              {editingContactId ? (
                <button type="button" className="gg-button gg-button--ghost" onClick={handleCancelEdit}>
                  Cancel Edit
                </button>
              ) : null}
            </div>
          </form>
        )}
        {message ? (
          <p
            className={`gg-inline-alert ${
              message.toLowerCase().includes('unable')
                ? 'gg-inline-alert--error'
                : message.toLowerCase().includes('unavailable')
                  ? 'gg-inline-alert--warning'
                  : 'gg-inline-alert--success'
            }`}
          >
            {message}
          </p>
        ) : null}
      </section>

      <section className="gg-module-panel">
        <p className="gg-module-panel__label">Contacts Directory</p>
        {contactsUnavailableMessage ? (
          <EmptyState
            title="Contacts directory unavailable"
            message="Contacts records cannot be loaded in this environment because the contacts table is not present."
          />
        ) : contacts.length === 0 ? (
          <EmptyState title="No contacts" message="Create a contact to link site/job communication paths." />
        ) : (
          <div className="gg-record-list">
            {contacts.map((contact) => {
              const linkedSite = contact.linked_site_id ? siteById.get(String(contact.linked_site_id)) : null
              const linkedJob = contact.linked_job_id ? jobById.get(String(contact.linked_job_id)) : null
              return (
                <article key={contact.id} className="gg-record-item">
                  <div className="gg-record-item__head">
                    <div>
                      <p className="gg-record-item__title">{contact.full_name}</p>
                      <p className="gg-record-item__meta">
                        {contact.company_name || 'No company'} • {contact.role_title || 'No role'}
                      </p>
                      <p className="gg-record-item__meta">
                        {contact.phone || 'No phone'} • {contact.email || 'No email'}
                      </p>
                      <p className="gg-record-item__meta">
                        {formatTitleText(contact.contact_type, 'General')} • Preferred {formatTitleText(contact.preferred_contact_method, 'None')}
                      </p>
                      <p className="gg-record-item__meta">
                        Site {formatTitleText(linkedSite?.name, 'Not linked')} • Job {formatTitleText(linkedJob?.job_type, linkedJob?.id || 'Not linked')}
                      </p>
                      <p className="gg-record-item__meta">Updated {formatDateTime(contact.updated_at || contact.created_at)}</p>
                    </div>
                    <StatusBadge status={contact.active_status !== false ? 'success' : 'paused'} label={contact.active_status !== false ? 'Active' : 'Inactive'} />
                  </div>
                  {contact.notes ? <p className="gg-record-item__meta">{contact.notes}</p> : null}
                  <div className="gg-form-actions">
                    <button type="button" className="gg-button gg-button--ghost" onClick={() => handleEdit(contact)} disabled={saving}>
                      Edit
                    </button>
                    <button type="button" className="gg-button gg-button--ghost" onClick={() => handleToggleActive(contact)} disabled={saving}>
                      {contact.active_status !== false ? 'Deactivate' : 'Activate'}
                    </button>
                  </div>
                </article>
              )
            })}
          </div>
        )}
      </section>
    </section>
  )
}
